import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import styled from '@emotion/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import { colors, overlayColors } from '@revenuemarket/design';
import { Typography, Tooltip } from '@revenuemarket/ui';
import { SystemInfo } from '@revenuemarket/ui/Icon';
import { LandingTypography } from '@revenuemarket/ui/Typography';
import { useIsMobile } from '@revenuemarket/utils';
import NextLink from 'next/link';
import { CalculatorFormData, PayoutEstimationResponse } from '~apis/estimate-calculate';
import { css } from '~libs/emotion';
import { mixpanel } from '~pages/_app';

// TODO: Refactor to resolve circular dependency
// oxlint-disable-next-line import/no-cycle
import { InputField } from './CalculatorForm';
import { InputFieldWrapper } from './CalculatorForm.style';
import { CALCULATOR_FORM_SCHEME } from './CalculatorForm.utils';
import { UnderYearlyRevenue, EstimatedLimit, UnderMinimumPayout } from './result';

interface CalculatorResultProps {
  data: CalculatorFormData;
  result: PayoutEstimationResponse;
  onRecalculate: (
    data: Pick<CalculatorFormData, 'monthlyAverageSpend' | 'cashAtHand' | 'monthlyAverageRevenue'>,
  ) => void;
}

const CalculatorResult = ({ data, result, onRecalculate }: CalculatorResultProps) => {
  const isMobile = useIsMobile();
  const topSectionComponent = useMemo(() => {
    switch (result.code) {
      case 'SUCCESS':
        return (
          <EstimatedLimit
            isMobile={isMobile}
            company={data.companyName}
            tradingPeriod={result.data.tradingPeriod}
            minPayoutLimit={result.data.minPayoutLimit}
            maxPayoutLimit={result.data.maxPayoutLimit}
          />
        );
      case 'UNDER_MINIMUM_PAYOUT_ESTIMATION':
        return <UnderMinimumPayout isMobile={isMobile} payoutLimit={result.data.maxPayoutLimit} />;
      case 'UNDER_YEARLY_REVENUE_THRESHOLD':
        return <UnderYearlyRevenue isMobile={isMobile} />;
    }
  }, [result.code, result.data, data.companyName, isMobile]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {topSectionComponent}
      <Link
        href={'https://app.clobe.ai'}
        onClick={() =>
          mixpanel.track('Click button - signup calculator', {
            email: data.email,
          })
        }
        style={{
          background: 'linear-gradient(90deg, #949ff9 0%, #4ec9c3 100%)',
          borderRadius: '8px',
        }}
      >
        {isMobile ? (
          <LandingTypography
            variant="h6"
            color={colors.grayscaleWhite}
            css={css`
              letter-spacing: -0.5px;
            `}
          >
            가입하고 정확한 한도 확인하기
          </LandingTypography>
        ) : (
          <Typography variant="h2" color={colors.grayscaleWhite}>
            가입하고 정확한 한도 확인하기
          </Typography>
        )}
      </Link>
      <RecalculateForm data={data} onRecalculate={onRecalculate} />
      <Notice
        variant="h8"
        color={colors.grayscale400}
        style={{ paddingTop: isMobile ? '24px' : '60px' }}
      >
        <p>
          위 예상 한도는 이해를 돕기 위해 단순화된 수치로, 실제 양도 조건과 크게 다를 수 있습니다.
          실제 양도 조건은 재무안정성, 매출건전성, 성장성, 수익성을 비롯한 여러 지표를 종합하여 판매
          가능 여부 및 양도 조건을 분석합니다.
        </p>
        <ol
          style={{
            listStyle: 'auto',
          }}
        >
          <li>
            회수 기간은 클로브 금융에서 양도대금을 이용할 수 있는 기간으로, 실제 회수 기간은 분석
            결과에 따라 1~12개월 사이에서 결정됩니다.{' '}
            <NextLink
              passHref
              href="https://intercom.help/clobe/ko/articles/8847464-레베뉴마켓-이용-및-판매-조건-안내"
              target="_blank"
              style={{ textDecoration: 'underline' }}
            >
              더 알아보기
            </NextLink>
          </li>
          <li>
            할인율은 예상 한도 계산으로는 파악하기 어려우며, 가입 후 한도 조회을 통해 확인할 수
            있습니다.
          </li>
        </ol>
      </Notice>
    </>
  );
};

export default CalculatorResult;

const RecalculateForm = ({
  data,
  onRecalculate,
}: Pick<CalculatorResultProps, 'data' | 'onRecalculate'>) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<
    Pick<CalculatorFormData, 'monthlyAverageRevenue' | 'monthlyAverageSpend' | 'cashAtHand'>
  >({
    resolver: yupResolver(
      CALCULATOR_FORM_SCHEME.pick(['monthlyAverageRevenue', 'cashAtHand', 'monthlyAverageSpend']),
    ),
    defaultValues: {
      monthlyAverageRevenue: data.monthlyAverageRevenue,
      cashAtHand: data.cashAtHand,
      monthlyAverageSpend: data.monthlyAverageSpend,
    },
  });

  const onSubmit = (
    data: Pick<CalculatorFormData, 'monthlyAverageRevenue' | 'monthlyAverageSpend' | 'cashAtHand'>,
  ) => {
    onRecalculate(data);
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <InputField
        control={control}
        errorMessage={errors?.monthlyAverageRevenue?.message}
        name="monthlyAverageRevenue"
        required
        type="number"
        align="right"
        label={
          <div data-tip data-for="monthlyAverageRevenue" style={{ display: 'flex', gap: '2px' }}>
            월평균 매출{' '}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <SystemInfo width={12} fill={colors.grayscaleWhite} />
            </div>
            <Tooltip effect="solid" id="monthlyAverageRevenue" place="right">
              회사의 최근 12개월 월평균 매출액
            </Tooltip>
          </div>
        }
        placeholder="100,000,000 원"
      />
      <InputField
        control={control}
        errorMessage={errors?.monthlyAverageSpend?.message}
        name="monthlyAverageSpend"
        required
        type="number"
        align="right"
        label={
          <div data-tip data-for="cash-burn" style={{ display: 'flex', gap: '2px' }}>
            월평균 비용{' '}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <SystemInfo width={12} fill={colors.grayscaleWhite} />
            </div>
            <Tooltip effect="solid" id="cash-burn" place="right">
              회사의 최근 12개월 월평균 영업 및 영업외비용
            </Tooltip>
          </div>
        }
        placeholder="10,000,000 원"
      />
      <InputField
        control={control}
        errorMessage={errors?.cashAtHand?.message}
        name="cashAtHand"
        required
        type="number"
        align="right"
        label={
          <div data-tip data-for="cashAtHand" style={{ display: 'flex', gap: '2px' }}>
            현재 보유 현금{' '}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <SystemInfo width={12} fill={colors.grayscaleWhite} />
            </div>
            <Tooltip effect="solid" id="cashAtHand" place="right">
              회사가 현재 보유한 입출금 및 외화 잔고 총액 또는 3개월 평균 잔고
            </Tooltip>
          </div>
        }
        placeholder="10,000,000 원"
      />
      <Button
        style={{
          background: colors.grayscale100,
        }}
      >
        <LandingTypography
          variant="h6"
          style={{
            color: colors.grayscale700,
          }}
        >
          다시 계산하기
        </LandingTypography>
      </Button>
    </Form>
  );
};

const Form = styled.form`
  box-sizing: border-box;
  margin: 120px auto 0;
  display: flex;
  gap: 24px;
  flex-direction: row;
  width: 800px;

  ${InputFieldWrapper} {
    width: 182px;

    @media (max-width: 500px) {
      width: 100%;
    }
  }

  @media (max-width: 500px) {
    flex-direction: column;
    width: 100%;
    padding: 0 24px;
  }
`;

const Link = styled.a`
  position: relative;
  box-sizing: border-box;
  display: flex;

  background-size: cover;
  width: 287px;
  height: 60px;
  margin: 40px auto 0;
  padding: 16px 24px;
  align-items: flex-start;
  gap: 16px;

  &::after {
    content: '';
    width: 600px;
    height: 600px;
    position: absolute;
    z-index: -999;
    top: 50%;
    left: 50%;
    background-image: url('https://static.clobe.ai/client_story_ladial.png');
    background-size: cover;
    background-position: center;
    transform: translate(-50%, -50%);
  }

  @media (max-width: 500px) {
    width: 225px;
    height: 47px;
    text-align: center;
    letter-spacing: -0.5px;
    padding: 12px 20px;
    justify-content: center;

    &::after {
      width: 400px;
      height: 400px;
    }
  }
`;

const Button = styled.button`
  display: flex;
  box-sizing: border-box;
  padding: 12px 20px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  gap: 12px;
  flex: 1 0 0;
  align-self: stretch;
  border-radius: 8px;
  background-color: ${overlayColors.white[4]};
  color: ${colors.grayscale100};

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: white;
  }
`;

const Notice = styled(LandingTypography)`
  width: 800px;
  border-top: 1px solid ${colors.grayscale550};
  margin: 0 auto;
  ol {
    padding-left: 16px;
  }

  @media (max-width: 500px) {
    box-sizing: border-box;
    width: calc(100% - 48px);
  }
`;
